<template>
  <div class="file-explorer-container">
    <div class="file-explorer">
      <div
        class="explorer-left"
        :class="{ 'has-selected-item': selectedFolderUri }"
      >
        <ul class="header-list">
          <li
            class="list-item root-item is-folder label is-size-7"
            :class="{ 'is-selected': !selectedFolderUri }"
            @click="selectLeftFolder(null)"
          >
            <a class="item-left">
              <span class="pr-4">Run Folders</span>
            </a>
            <span class="item-right">
              <span
                v-if="groupName"
                v-tooltip="`Group: ${groupName}`"
                class="tag is-primary"
                :class="{ 'is-light': !selectedFolderUri }"
              >
                <span>
                  {{ groupName }}
                </span>
              </span>
            </span>
          </li>
        </ul>
        <slot name="notifications" />
        <ul v-if="listedFiles.length" class="list-scrollable">
          <template
            v-for="(folder, index) in listedFiles"
            :key="`li${index}-${folder.name}`"
          >
            <li
              v-if="folder.is_dir"
              v-tooltip="
                folder.is_registered && `${folder.name} is already registered`
              "
              class="list-item is-folder"
              :class="{
                'is-selected': folder.isSelected,
                'is-disabled': !folder.isSelectable,
              }"
              @click="folder.isSelectable && selectLeftFolder(folder)"
            >
              <a
                class="item-left"
                :class="{ 'is-disabled': !folder.isSelectable }"
              >
                <span class="icon">
                  <i
                    class="fa-xs"
                    :class="[
                      folder.uri === selectedFolderUri
                        ? 'fa-regular fa-folder-open'
                        : 'fa fa-folder',
                    ]"
                  />
                </span>
                {{ folder.name }}
              </a>
              <span class="item-right">
                <span
                  v-if="
                    selectedData.length && folder.uri === selectedFilesFolderUri
                  "
                  class="tag is-rounded file-count-badge"
                  :class="folder.isSelected ? 'is-white' : 'is-primary'"
                >
                  {{ selectedData.length }}
                </span>
                <template v-if="folder.uri === selectedFolderUri">
                  <a
                    class="icon close-icon"
                    @click.stop="selectLeftFolder(null)"
                  >
                    <i class="fa fa-times" />
                  </a>
                  <span class="icon angle-icon">
                    <i class="fa fa-angle-right fa-sm" />
                  </span>
                </template>
              </span>
            </li>
            <li
              v-else
              v-tooltip="
                `A file cannot be selected as the run folder. If this is a
                dataset, please move it into the relevant run folder`
              "
              class="list-item is-disabled"
            >
              <span class="item-left">
                <span class="icon">
                  <i class="fa-regular fa-file fa-xs" />
                </span>
                {{ folder.name }}
              </span>
            </li>
          </template>
        </ul>
        <div v-else class="help p-3" style="min-width: 250px">
          No folder found in dropbox
        </div>
      </div>
      <div
        class="explorer-right"
        :class="{
          'is-loading': isLoadingFolderContent === true,
          'has-content': selectedFolderUri,
        }"
      >
        <div
          v-if="folderContentError && selectedFolderUri"
          class="notification is-danger m-4"
        >
          <span class="icon">
            <i class="fa-solid fa-exclamation-triangle" />
          </span>
          <span class="text">
            <strong>Error loading folder content:</strong>
            {{
              folderContentError.response?.data?.message ||
              folderContentError.response?.data?.detail ||
              folderContentError.message ||
              'Unknown error'
            }}
          </span>
        </div>
        <file-tree
          v-else-if="selectedFolderUri"
          :key="`file-explorer-${selectedLeftFolder.name}`"
          :root-node="selectedLeftFolder"
          :load-node="loadFolderContent"
          :selected-nodes="activeSelectedData"
          :can-select-root-node="canSelectRootFolder"
          :disabled="hasFilesInOtherFolder"
          tree-class="py-2 px-4"
          @select-nodes="selectData"
        >
          <template #toolbar>
            <span class="expl-opts">
              <slot name="explorer-options" />
            </span>
          </template>
          <template v-if="hasFilesInOtherFolder" #banner>
            <div class="notification is-warning is-light other-folder-warning">
              <span class="icon">
                <i class="fa-solid fa-exclamation-triangle" />
              </span>
              <div>
                <p>
                  <strong>{{ selectedData.length }}</strong>
                  dataset(s) already selected in
                  <code>{{ selectedFilesFolderName }}</code>
                  . Datasets from different folders cannot be mixed. Selecting
                  datasets in this folder has been disabled until currently
                  selected datasets are discarded.
                </p>
                <button
                  class="button is-warning is-small mt-2"
                  @click="resetSelectedFiles"
                >
                  <span class="icon is-small">
                    <i class="fa-solid fa-trash fa-xs" />
                  </span>
                  <span>Discard selected datasets</span>
                </button>
              </div>
            </div>
          </template>
          <template v-if="$slots['tree-label']" #tree-label="slotsProps">
            <slot name="tree-label" v-bind="slotsProps" />
          </template>
        </file-tree>
        <div
          v-else
          class="init-info"
          style="margin: auto auto; padding: 2em; text-align: center"
        >
          <slot name="explorer-right-info" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { alphaNumSort } from '@/utils'

import FileTree from '@/components/FileTree.vue'

const sortNonRegisteredFirst = (a, b) => {
  const aIsRegistered = a.is_registered
  const bIsRegistered = b.is_registered
  if (aIsRegistered === bIsRegistered) return 0
  else if (aIsRegistered) return 1
  else if (bIsRegistered) return -1
}

const sortFolderFirst = (a, b) => {
  const aFolder = a.is_dir
  const bFolder = b.is_dir
  if (aFolder && bFolder) return 0
  else if (aFolder && !bFolder) return -1
  else if (!aFolder && bFolder) return 1
}

const sortContent = (content) => {
  content = content || []
  return content
    .sort((a, b) => alphaNumSort(a.name, b.name))
    .sort(sortNonRegisteredFirst)
    .sort(sortFolderFirst)
}

export default {
  name: 'FileExplorer',
  components: {
    FileTree,
  },
  props: {
    rootFolder: { type: Object },
    fetchFolderContent: { type: Function },
    selectLeftFolder: { type: Function },
    selectedLeftFolder: { type: Object },
    selectData: { type: Function },
    selectedData: { type: Array, default: () => [] },
    selectedFilesFolderUri: { type: String, default: null },
    selectedFilesFolderName: { type: String, default: null },
    canSelectRootFolder: { type: Boolean, default: false },
    resetSelectedFiles: { type: Function, default: null },
  },
  data() {
    return {
      isLoadingFolderContent: false,
      folderContentError: null,
    }
  },
  computed: {
    groupName() {
      return this.rootFolder.group?.value?.name
    },
    listedFiles() {
      let files = this.rootFolder.files.map((f) => ({
        ...f,
        isSelected: this.selectedFolderUri === f.uri,
      }))
      return sortContent(files)
    },
    selectedFolderUri() {
      return this.selectedLeftFolder?.uri
    },
    hasFilesInOtherFolder() {
      return (
        this.selectedData.length > 0 &&
        this.selectedFilesFolderUri &&
        this.selectedFolderUri !== this.selectedFilesFolderUri
      )
    },
    activeSelectedData() {
      return this.hasFilesInOtherFolder ? [] : this.selectedData
    },
  },
  methods: {
    async loadFolderContent(folder) {
      this.folderContentError = null
      this.isLoadingFolderContent = true
      try {
        return await this.fetchFolderContent(folder)
      } catch (error) {
        console.error('Error loading folder content:', error)
        this.folderContentError = error
        return []
      } finally {
        this.isLoadingFolderContent = false
      }
    },
  },
  watch: {
    selectedFolderUri: {
      handler: function () {
        this.$nextTick(() => {
          try {
            this.$el.getElementsByClassName('is-selected')[0].scrollIntoView({
              behavior: 'smooth',
              inline: 'end',
            })
          } catch (e) {
            console.error(`Auto scroll failed ${e}`)
          }
        })
      },
      immediate: true,
    },
  },
}
</script>

<style lang="scss" scoped>
.file-explorer-container {
  height: 100%;
  overflow: hidden;
  user-select: none;
}

.file-explorer {
  display: inline-flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.file-explorer .explorer-right {
  flex-grow: 1;
  flex-shrink: 1;
  overflow: hidden;

  &:not(.has-content) {
    border-top: 1px solid var(--color-border-default);
  }

  .file-tree-wrapper {
    width: 100%;
    height: 100%;
  }
}

.file-explorer .explorer-left {
  position: relative;
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--color-border-default);
  border-right: 1px solid var(--color-border-default);
  flex-basis: auto;
  width: 500px;
  min-width: 200px;
  max-width: 66%;
  resize: horizontal;
  overflow-x: hidden;
  overflow-y: auto;
  height: 100%;
  padding-bottom: 2rem;

  ul {
    & > li {
      display: flex;
      align-items: center;
      padding: 0.2rem 1rem;
      cursor: pointer;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
      background-color: var(--white);

      &:not(.is-selected):not(.is-disabled):hover {
        background-color: var(--brand-light);
      }

      a {
        color: var(--gray-600);
      }

      .item-left {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .item-right {
        display: flex;
        align-items: center;
        margin-left: auto;

        & > *:first-child {
          margin-right: 0.5rem;
        }
        .close-icon {
          display: none;
        }
      }
      & > .icon {
        padding: 0 1rem;
      }

      &.is-folder:not(.root-item) .item-left::after {
        content: '/';
      }

      &.is-selected {
        color: var(--white);
        background-color: var(--brand);
        & > a,
        & > a.is-disabled {
          opacity: 1;
          color: var(--white) !important;
        }
      }

      &.is-disabled,
      &:not(.is-folder) {
        pointer-events: all !important;
        cursor: not-allowed;
      }
    }

    &:not(.header-list) li.list-item {
      &:not(:last-of-type)::before {
        content: '⊢';
        margin-right: 3px;
      }
      &:last-of-type::before {
        content: '∟';
        margin-top: -4px;
        font-size: 0.9rem;
      }
    }

    &.header-list {
      position: sticky;
      top: 0;
      > li.root-item {
        height: 40px;
        padding: 0.5rem;
        padding-right: 5px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 100%;
        min-width: 100%;
        border-bottom: 1px solid var(--color-border-default);

        &.label {
          padding: 0.2rem 0.7rem;
        }

        .item-left {
          flex-shrink: 0;
        }
        .item-right {
          max-width: 44%;
        }
        .tag {
          font-size: 0.7rem;
          font-weight: 900;
          overflow: hidden;
          width: 100%;
          span {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }
    }
  }
}

@media (max-width: 1280px) {
  .file-explorer {
    display: flex;
    flex-direction: column;
  }
  .file-explorer .explorer-right {
    overflow: hidden;
  }

  .file-explorer .explorer-left {
    border-top: 1px solid var(--color-border-default);
    overflow-y: auto;
    resize: none;
    width: 100% !important;
    max-width: 100%;
    flex-shrink: 0;
    padding: 0;

    &.has-selected-item {
      height: fit-content;
      flex-basis: unset;
      padding: 0;
      ul.list-scrollable {
        li.is-selected {
          padding: 0.5rem;
          &::before {
            content: '∟';
            margin-top: -4px;
            font-size: 0.9rem;
          }
          .item-right {
            .angle-icon {
              display: none;
            }
            .close-icon {
              display: flex;
            }
          }
        }

        li:not(.is-selected) {
          display: none;
        }
      }
    }
  }
}

:deep(.other-folder-warning) {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  margin: 0;
  padding: var(--space-2) var(--space-3);
  border-radius: 0;
  border-left: none;
  border-right: none;
}

.file-count-badge {
  font-size: 0.6rem;
  font-weight: 700;
}

.file-explorer .explorer-right.is-loading {
  color: gray;
}

.expl-opts {
  padding: 0.5rem;
  display: flex;
  border-bottom: 1px solid var(--color-border-default);
  background-color: var(--color-background-default) !important;
}
</style>
